export const MARKDOWN_MAX_LENGTH = 20000;

const MAX_LINE_LENGTH = 2000;
const MAX_LINE_COUNT = 1000;
const MAX_CONTAINER_DEPTH = 32;
const MAX_CONTAINER_MARKERS = 1000;
const MAX_INDENTATION_COLUMNS = 100;
const MAX_FORMATTING_DELIMITERS = 2000;
const MAX_AUTOLINKS = 200;

const unsafeMarkdownMessage =
  'This Markdown is too complex to render safely. Shorten long lines and reduce nested quotes, lists, or formatting.';

export const getEffectiveMarkdownMaxLength = (maxLength?: number) => {
  if (maxLength === undefined || !Number.isFinite(maxLength)) {
    return MARKDOWN_MAX_LENGTH;
  }

  return Math.min(Math.max(1, Math.floor(maxLength)), MARKDOWN_MAX_LENGTH);
};

const indentationColumns = (line: string) => {
  const indentation = line.match(/^[ \t]*/)?.[0] || '';

  return Array.from(indentation).reduce(
    (columns, character) => columns + (character === '\t' ? 4 : 1),
    0
  );
};

const containerDepth = (line: string) => {
  let remaining = line.trimStart();
  let depth = 0;

  while (remaining) {
    const blockquote = remaining.match(/^>[ \t]?/);
    const unorderedList = remaining.match(/^[-+*][ \t]+/);
    const orderedList = remaining.match(/^\d{1,9}[.)][ \t]+/);
    const marker = blockquote || unorderedList || orderedList;

    if (!marker) {
      break;
    }

    depth += 1;
    if (depth > MAX_CONTAINER_DEPTH) {
      return depth;
    }
    remaining = remaining.slice(marker[0].length);
  }

  return depth;
};

export const getMarkdownSafetyError = (
  markdown: string,
  maxLength?: number
): string | undefined => {
  const effectiveMaxLength = getEffectiveMarkdownMaxLength(maxLength);
  if (markdown.length > effectiveMaxLength) {
    return `Markdown cannot exceed ${effectiveMaxLength.toLocaleString()} characters.`;
  }

  const lines = markdown.split('\n');
  if (lines.length > MAX_LINE_COUNT) {
    return unsafeMarkdownMessage;
  }

  let formattingDelimiters = 0;
  let containerMarkers = 0;
  let autolinks = 0;

  for (const line of lines) {
    const lineContainerDepth = containerDepth(line);
    if (
      line.length > MAX_LINE_LENGTH ||
      indentationColumns(line) > MAX_INDENTATION_COLUMNS ||
      lineContainerDepth > MAX_CONTAINER_DEPTH
    ) {
      return unsafeMarkdownMessage;
    }

    containerMarkers += lineContainerDepth;
    formattingDelimiters += (line.match(/[\[\]()*_~`<>|]/g) || []).length;
    autolinks += (line.match(/(?:https?:\/\/|www\.|@)/gi) || []).length;
    if (
      containerMarkers > MAX_CONTAINER_MARKERS ||
      formattingDelimiters > MAX_FORMATTING_DELIMITERS ||
      autolinks > MAX_AUTOLINKS
    ) {
      return unsafeMarkdownMessage;
    }
  }

  return undefined;
};
